import React, { useEffect, useState, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { BarChart3, ShieldCheck, BookOpen, Zap, Cpu, TrendingUp, X, Minus, PanelLeftClose, PanelRightClose, ChevronDown, Check, LogOut } from 'lucide-react';
interface Service {
  id: string;
  name: string;
}
interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}
export function Sidebar({
  isOpen = false,
  onClose,
  isCollapsed = false,
  onToggleCollapse
}: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isHovering, setIsHovering] = useState(false);
  const [selectedService, setSelectedService] = useState<Service>({
    id: 'service-1',
    name: 'Service 1'
  });
  const [isServiceDropdownOpen, setIsServiceDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const services: Service[] = [{
    id: 'service-1',
    name: 'Service 1'
  }, {
    id: 'service-2',
    name: 'Service 2'
  }, {
    id: 'service-3',
    name: 'Service 3'
  }, {
    id: 'service-4',
    name: 'Service 4'
  }, {
    id: 'service-5',
    name: 'Service 5'
  }];
  const menuItems = [{
    id: 'panoramica',
    label: 'Panoramica',
    icon: BarChart3,
    path: '/overview'
  }, {
    id: 'policy',
    label: 'Policy',
    icon: ShieldCheck,
    path: '/policy'
  }, {
    id: 'knowledge',
    label: 'Knowledge',
    icon: BookOpen,
    path: '/knowledge'
  }, {
    id: 'actions',
    label: 'Actions',
    icon: Zap,
    path: '/actions'
  }, {
    id: 'models',
    label: 'Models',
    icon: Cpu,
    path: '/models'
  }, {
    id: 'consumi',
    label: 'Consumi',
    icon: TrendingUp,
    path: '/consumi'
  }];
  // Handle closing sidebar when route changes on mobile
  useEffect(() => {
    if (onClose) {
      onClose();
    }
  }, [location.pathname]);
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsServiceDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Determine if sidebar should show expanded content
  const showExpanded = !isCollapsed || isHovering;
  const handleServiceSelect = (service: Service) => {
    setSelectedService(service);
    setIsServiceDropdownOpen(false);
  };
  const handleLogout = () => {
    // Add logout logic here
    console.log('Logout clicked');
    // For now, just navigate to login or home
    navigate('/');
  };
  return <>
      {/* Mobile Backdrop */}
      <div className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-30 lg:hidden transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={onClose} data-id="element-625" />

      {/* Sidebar Container */}
      <aside className={`fixed left-0 top-0 h-screen bg-white dark:bg-dark-surface border-r border-gray-200 dark:border-dark-subtle flex flex-col transition-all duration-300 ease-out lg:translate-x-0 ${isOpen ? 'translate-x-0 z-40' : '-translate-x-full'} ${isCollapsed && isHovering ? 'lg:z-50' : 'lg:z-40'} ${showExpanded ? 'w-[260px]' : 'lg:w-[80px]'}`} onMouseEnter={() => isCollapsed && setIsHovering(true)} onMouseLeave={() => isCollapsed && setIsHovering(false)} data-id="element-626">
        {/* Logo/Brand Section */}
        <div className="border-b border-gray-100 dark:border-dark-subtle px-6 pt-6 pb-5 flex flex-col gap-4" data-id="element-627">
          {showExpanded ? <>
              {/* Logo and Collapse Button Row */}
              <div className="flex items-start justify-between" data-id="element-628">
                <div className="flex flex-col" data-id="element-629">
                  <img src="https://cdn.magicpatterns.com/uploads/pUnvTWzSVeUj8kzGcTgfuu/EIIS_Intelligence.svg" alt="EIIS Intelligence Logo" className="h-16 w-auto p-0 -ml-[3px]" data-id="element-630" />
                  <p className="text-[12px] text-gray-400 dark:text-gray-500 mt-2" data-id="element-631">
                    Powered by Dmood Engine AI
                  </p>
                </div>

                {/* Collapse button - aligned to top */}
                <button onClick={onToggleCollapse} className="hidden lg:flex p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-hover rounded-xl transition-colors flex-shrink-0" title={isCollapsed && isHovering ? 'Mantieni espansa' : 'Comprimi sidebar'} data-id="element-632">
                  {isCollapsed && isHovering ? <PanelRightClose className="w-4 h-4" data-id="element-633" /> : <PanelLeftClose className="w-4 h-4" data-id="element-634" />}
                </button>

                {/* Mobile Close Button */}
                <button onClick={onClose} className="lg:hidden p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-hover rounded-xl transition-colors flex-shrink-0" data-id="element-635">
                  <X className="w-5 h-5" data-id="element-636" />
                </button>
              </div>

              {/* Service Selector - Custom Dropdown */}
              <div className="relative" ref={dropdownRef} data-id="element-637">
                <button onClick={() => setIsServiceDropdownOpen(!isServiceDropdownOpen)} className="w-full bg-white dark:bg-dark-surface border border-gray-200/60 dark:border-dark-medium/60 text-gray-900 dark:text-gray-100 py-3 pl-4 pr-10 rounded-xl text-base font-medium hover:border-gray-300 dark:hover:border-dark-medium transition-all duration-200 text-left flex items-center justify-between" data-id="element-638">
                  <span data-id="element-639">{selectedService.name}</span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 dark:text-gray-500 transition-transform duration-200 ${isServiceDropdownOpen ? 'rotate-180' : ''}`} strokeWidth={2.5} data-id="element-640" />
                </button>

                {/* Custom Dropdown Menu */}
                {isServiceDropdownOpen && <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-subtle rounded-xl overflow-hidden z-50" data-id="element-641">
                    {services.map(service => <button key={service.id} onClick={() => handleServiceSelect(service)} className={`w-full px-4 py-2.5 text-left text-base font-medium transition-colors duration-150 flex items-center justify-between ${selectedService.id === service.id ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-hover'}`} data-id="element-642">
                        <span data-id="element-643">{service.name}</span>
                        {selectedService.id === service.id && <Check className="w-4 h-4" strokeWidth={2.5} data-id="element-644" />}
                      </button>)}
                  </div>}
              </div>
            </> : <div className="flex items-center justify-center w-full" data-id="element-645">
              <img src="https://cdn.magicpatterns.com/uploads/pUnvTWzSVeUj8kzGcTgfuu/EIIS_Intelligence.svg" alt="EIIS Intelligence Logo" className="h-14 w-auto p-0 -ml-[3px]" data-id="element-646" />
            </div>}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-6 px-6 custom-scrollbar" data-id="element-647">
          <div className="space-y-1" data-id="element-648">
            {menuItems.map(item => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return <NavLink key={item.id} to={item.path} className={`relative flex items-center ${showExpanded ? 'px-3' : 'justify-center px-3'} py-2.5 text-[14px] transition-all duration-180 group select-none touch-manipulation ${isActive ? 'bg-gray-800 dark:bg-gray-700 text-white font-medium rounded-r-xl' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-hover hover:text-gray-900 dark:hover:text-gray-200 rounded-xl'}`} title={!showExpanded ? item.label : undefined} data-id="element-649">
                  {/* Green accent bar on the left for active item */}
                  {isActive && <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500" data-id="element-650" />}

                  <Icon className={`w-[20px] h-[20px] lg:w-[18px] lg:h-[18px] ${showExpanded ? 'mr-3' : ''} transition-colors flex-shrink-0 ${isActive ? 'text-white' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300'}`} strokeWidth={2} data-id="element-651" />
                  {showExpanded && <span className="whitespace-nowrap overflow-hidden text-ellipsis" data-id="element-652">
                      {item.label}
                    </span>}
                </NavLink>;
          })}
          </div>
        </nav>

        {/* Footer - Logout Button */}
        <div className="p-4 border-t border-gray-100 dark:border-dark-subtle" data-id="element-653">
          {showExpanded ? <button onClick={handleLogout} className="w-full flex items-center p-3 lg:p-2 rounded-xl hover:bg-red-50 dark:hover:bg-red-900/10 text-gray-700 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 cursor-pointer transition-colors duration-180 group" data-id="element-654">
              <LogOut className="w-5 h-5 mr-3 text-gray-400 dark:text-gray-500 group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors" strokeWidth={2} data-id="element-655" />
              <span className="text-sm font-medium" data-id="element-656">Logout</span>
            </button> : <button onClick={handleLogout} className="w-full flex items-center justify-center p-2 rounded-xl hover:bg-red-50 dark:hover:bg-red-900/10 cursor-pointer transition-colors duration-180 group" title="Logout" data-id="element-657">
              <LogOut className="w-5 h-5 text-gray-400 dark:text-gray-500 group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors" strokeWidth={2} data-id="element-658" />
            </button>}
        </div>
      </aside>
    </>;
}